import * as React from 'react'
import {
  type ColumnDef,
  type Column,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import {
  ArrowUpDown,
  ChevronRight,
  Filter,
  X,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect as Select } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { CallsQueryParams } from '../hooks/useCallsQuery'
import {
  filterFields as defaultFilterFields,
  getFieldConfig as defaultGetFieldConfig,
  getOperatorsForField as defaultGetOperatorsForField,
  hasActiveFilters,
  operatorLabels,
  type FilterCondition,
  type FilterField,
  type FilterOperator,
  type FilterFieldConfig,
} from '../utils/filterUtils'

// ------------- SORTING -------------

// Helper function to create a sortable header
const createSortableHeader = (label: string) => {
  return ({ column }: { column: Column<unknown> }) => {
    const isSorted = column.getIsSorted()
    return (
      <Button
        variant="ghost"
        className="group !p-0 hover:!bg-transparent"
        onClick={() => {
          // Toggle sorting: if not sorted or descending, go to ascending; if ascending, go to descending
          if (isSorted === 'asc') {
            column.toggleSorting(true) // Switch to descending
          } else {
            column.toggleSorting(false) // Switch to ascending (or set ascending if not sorted)
          }
        }}>
        {label}{' '}
        <ArrowUpDown
          size={16}
          className={cn(
            'transition-opacity',
            isSorted ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          )}
        />
      </Button>
    )
  }
}

// Generic helper + type to process columns and add sortable headers
// where labels are defined. Feature modules can use these to avoid
// duplicating the sortable header wiring.
export type ColumnDefWithLabel<TData> = ColumnDef<TData> & { label?: string }

export function sortableColumns<TData>(
  columnDefs: ColumnDefWithLabel<TData>[]
): ColumnDef<TData>[] {
  return columnDefs.map((col) => {
    if (col.label && !col.header) {
      const { label, ...rest } = col
      return {
        ...rest,
        header: createSortableHeader(label),
        // Preserve any existing meta and attach the label for use in
        // column-visibility UIs.
        meta: { ...(rest as any).meta, label },
      } as ColumnDef<TData>
    }
    const { label, ...rest } = col
    return {
      ...rest,
      meta: label ? { ...(rest as any).meta, label } : (rest as any).meta,
    } as ColumnDef<TData>
  })
}

type DataTableProps = {
  /**
   * Whether to show the Filters UI in the toolbar.
   */
  showFilters?: boolean
  /**
   * Result of the query (from useCallsQuery / similar) passed in by the
   * container. This keeps DataTable reusable/presentational.
   */
  // Intentionally accept 'any' here to avoid tightly coupling this
  // component to a specific React Query result type. The feature
  // container is responsible for passing a compatible shape.
  queryResult: any
  /**
   * Callback to inform the parent whenever query params change so it
   * can call useCallsQuery with the latest params.
   */
  onQueryParamsChange?: (params: CallsQueryParams) => void
  /**
   * Column definitions for the table (including any action columns).
   * Feature containers are responsible for defining these and passing
   * them in.
   */
  columns: ColumnDef<any>[]
  /**
   * Optional function to derive a stable row key for features like
   * "new row" highlighting. Feature containers should provide this
   * (e.g. call_id, id, etc). If omitted, we fall back to row.id.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowKey?: (row: any) => string | undefined
  /**
   * Optional React elements to render at the end of the toolbar.
   * Useful for adding page-specific actions like Import buttons.
   */
  toolbarActions?: React.ReactNode
  /**
   * Optional client-side page size for pagination UI.
   * Defaults to 25 if not provided.
   */
  pageSize?: number
  /**
   * Optional backend limit for query params.
   * Defaults to the pageSize when not provided.
   */
  queryLimit?: number
  /**
   * Optional default sort column id for initial query params.
   */
  defaultSortBy?: string
  /**
   * Optional default sort order when no sorting is set.
   */
  defaultSortOrder?: 'asc' | 'desc'
  /**
   * Optional React Query key to invalidate on refresh.
   */
  refreshQueryKey?: QueryKey
  /**
   * Optional empty state label for the table body.
   */
  emptyStateLabel?: string
  /**
   * Optional explicit total result count from pagination metadata.
   * When provided, this is used for the toolbar "Total results" value.
   */
  totalCount?: number | null
  /**
   * Optional row click handler.
   */
  onRowClick?: (row: any) => void
  /**
   * Enable checkbox multi-select for rows.
   */
  enableMultiSelect?: boolean
  /**
   * Callback with currently selected rows.
   */
  onSelectedRowsChange?: (rows: any[]) => void
  /**
   * Optional async loader for expandable sub-rows. When provided and
   * expandRowOnClick is true, clicking a row toggles an expanded section and
   * loads sub-row data on first expand.
   */
  fetchSubRows?: (row: any) => Promise<any[]>
  /**
   * Optional renderer for expanded sub-row content.
   */
  renderSubRows?: (subRows: any[], row: any) => React.ReactNode
  /**
   * Optional override to enable row expansion on click.
   * Defaults to true when fetchSubRows is provided.
   */
  expandRowOnClick?: boolean
  /**
   * Optional predicate to control which rows are expandable.
   * Defaults to all rows when expansion is enabled.
   */
  canExpandRow?: (row: any) => boolean
  /**
   * Optional filter field configurations.
   * If not provided, defaults to Calls table filter fields for backward compatibility.
   */
  filterFieldConfigs?: FilterFieldConfig[]
  /**
   * Optional initial filters to restore filter state (e.g., from URL params).
   */
  initialFilters?: FilterCondition[]
}

export function DataTable({
  showFilters = true,
  queryResult,
  onQueryParamsChange,
  columns,
  getRowKey,
  toolbarActions,
  pageSize: pageSizeProp,
  queryLimit,
  defaultSortBy,
  defaultSortOrder,
  refreshQueryKey,
  emptyStateLabel,
  totalCount,
  onRowClick,
  enableMultiSelect = false,
  onSelectedRowsChange,
  fetchSubRows,
  renderSubRows,
  expandRowOnClick,
  canExpandRow,
  filterFieldConfigs,
  initialFilters,
}: DataTableProps) {
  const queryClient = useQueryClient()

  // Use provided filter configs or fall back to default Calls filter fields
  const filterFields = filterFieldConfigs ?? defaultFilterFields

  // Helper functions that use the dynamic filter fields
  const getFieldConfig = (field: string): FilterFieldConfig | undefined => {
    if (filterFieldConfigs) {
      return filterFieldConfigs.find((f) => f.value === field)
    }
    return defaultGetFieldConfig(field as FilterField)
  }

  const getOperatorsForField = (field: string): FilterOperator[] => {
    if (filterFieldConfigs) {
      // Generic operators based on field type
      const fieldConfig = getFieldConfig(field)
      const baseOperators: FilterOperator[] = ['=']
      if (fieldConfig?.type === 'number' || fieldConfig?.type === 'date') {
        baseOperators.push('>', '<', '>=', '<=')
      } else if (fieldConfig?.type === 'options') {
        baseOperators.push('!=', 'in', 'not_in')
      } else {
        baseOperators.push('contains', 'not_contains')
      }
      return baseOperators
    }
    return defaultGetOperatorsForField(field as FilterField)
  }

  // Track last updated time for data refresh indicator
  const [lastUpdated, setLastUpdated] = React.useState<Date>(new Date())
  // Track new rows for highlighting (keyed by a stable row identifier:
  // uses call_id when present, otherwise falls back to id – so this works
  // for both Call Logs and Patients List).
  const [newRowKeys, setNewRowKeys] = React.useState<Set<string>>(new Set())
  const previousRowKeysRef = React.useRef<Set<string>>(new Set())
  const onSelectedRowsChangeRef = React.useRef(onSelectedRowsChange)
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<Set<string>>(
    new Set()
  )
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<Set<string>>(
    new Set()
  )
  const [subRowsByParentKey, setSubRowsByParentKey] = React.useState<
    Record<
      string,
      {
        isLoading: boolean
        data: any[] | null
        error: string | null
      }
    >
  >({})

  const pageSize = pageSizeProp ?? 25
  const limit = queryLimit ?? pageSize
  const [currentPage, setCurrentPage] = React.useState(1)
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  // ------------- FILTERING -------------
  // Pending filters (being available to be edited in UI)
  const [filters, setFilters] = React.useState<FilterCondition[]>(
    initialFilters || []
  )
  // Applied filters (actually used for filtering data)
  const [appliedFilters, setAppliedFilters] = React.useState<FilterCondition[]>(
    initialFilters || []
  )
  const [filterDropdownOpen, setFilterDropdownOpen] = React.useState(false)

  // Add a new filter
  const addFilter = () => {
    // Use first available filter field, or fallback to first column's accessorKey
    let defaultField: string
    if (filterFields.length > 0) {
      defaultField = filterFields[0]!.value
    } else {
      const firstColumn = columns[0]
      defaultField =
        firstColumn &&
        'accessorKey' in firstColumn &&
        typeof firstColumn.accessorKey === 'string'
          ? firstColumn.accessorKey
          : 'id'
    }

    const newFilter: FilterCondition = {
      id: `filter-${Date.now()}-${Math.random()}`,
      field: defaultField as any,
      operator: '=',
      value: '',
    }
    setFilters([...filters, newFilter])
  }

  // Remove a filter
  const removeFilter = (id: string) => {
    setFilters(filters.filter((f) => f.id !== id))
  }

  // Update a filter
  const updateFilter = (id: string, updates: Partial<FilterCondition>) => {
    setFilters(filters.map((f) => (f.id === id ? { ...f, ...updates } : f)))
  }

  // Apply filters - copy pending filters to applied filters
  const applyFilters = () => {
    // Only apply filters that have values, and trim all filter values
    // For Type field, check if at least one checkbox is selected
    const filtersToApply = filters
      .filter((f) => {
        if (f.field === 'type') {
          // For Type, check if at least one checkbox is selected
          const selectedTypes = f.value
            .split(',')
            .map((v) => v.trim().toLowerCase())
            .filter(Boolean)
          return selectedTypes.length > 0
        }
        return f.value.trim()
      })
      .map((f) => {
        // For Type field, ensure values are lowercase and comma-separated
        if (f.field === 'type') {
          const selectedTypes = f.value
            .split(',')
            .map((v) => v.trim().toLowerCase())
            .filter(Boolean)
          return { ...f, value: selectedTypes.join(',') }
        }
        return { ...f, value: f.value.trim() }
      })

    setAppliedFilters(filtersToApply)
    setFilterDropdownOpen(false)
  }

  // Clear all filters
  const clearFilters = () => {
    setFilters([])
    setAppliedFilters([])
    setFilterDropdownOpen(false)
  }

  // Check if filters are active (based on applied filters, not pending ones)
  const hasActiveFiltersValue = hasActiveFilters(appliedFilters)
  const canApplyFilters = React.useMemo(() => {
    // Allow applying when there are no pending filters if we currently have
    // active filters; this enables "remove all filters" to clear URL/query state.
    if (filters.length === 0) {
      return hasActiveFiltersValue
    }
    return filters.some((f) => f.value.trim())
  }, [filters, hasActiveFiltersValue])

  // Handle refresh button click
  const handleRefresh = () => {
    if (!refreshQueryKey) return
    queryClient.invalidateQueries({ queryKey: refreshQueryKey })
    setLastUpdated(new Date())
    toast.success('Refreshing data...')
  }

  // Format time ago for last updated indicator
  const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)
    if (seconds < 60) {
      return 'a few moments ago'
    }
    const minutes = Math.floor(seconds / 60)
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
  }

  // Update last updated time display every 10 seconds
  const [timeAgoText, setTimeAgoText] = React.useState(
    formatTimeAgo(lastUpdated)
  )
  React.useEffect(() => {
    // Update immediately when lastUpdated changes
    setTimeAgoText(formatTimeAgo(lastUpdated))

    // Then update every 10 seconds
    const interval = setInterval(() => {
      setTimeAgoText(formatTimeAgo(lastUpdated))
    }, 10000) // Update every 10 seconds
    return () => clearInterval(interval)
  }, [lastUpdated])

  /**
   * Map a table column id to the backend sort_by field.
   *
   * Priority:
   * 1) Column meta: meta.sortByField (recommended for all sortable columns)
   * 2) Fallback: use the column id itself (generic, no table-specific logic)
   */
  const getSortByField = (columnId: string): string => {
    // Column-level configuration via meta.sortByField for maximum reusability
    const columnDef = columns.find((col) => {
      const colId = (col as any).id ?? (col as any).accessorKey
      return colId === columnId
    })

    const metaSortBy =
      columnDef && typeof (columnDef as any).meta?.sortByField === 'string'
        ? ((columnDef as any).meta.sortByField as string)
        : null

    if (metaSortBy && metaSortBy.length > 0) {
      return metaSortBy
    }

    // Generic fallback: use the column id as the sort_by field.
    return columnId || 'created_at'
  }

  // Fetch calls data with sorting and search filters
  const sortColumnId = sorting[0]?.id || defaultSortBy || 'created_at'
  const sortBy = getSortByField(sortColumnId)
  // In TanStack Table: desc: false = ascending, desc: true = descending, undefined = no sort (default to desc)
  const sortOrder: 'asc' | 'desc' =
    sorting[0] === undefined
      ? (defaultSortOrder ?? 'desc') // Default to descending when no sort is set
      : sorting[0].desc === false
        ? 'asc'
        : 'desc'

  // Memoize query params to ensure stable reference and proper refetching
  const queryParams = React.useMemo(() => {
    const outboundFilters =
      appliedFilters.length > 0
        ? appliedFilters.map((filter) => {
            if (
              (filter.operator === '!=' || filter.operator === 'not_in') &&
              filter.value.trim()
            ) {
              const normalizedValue = filter.value
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean)
                .map((v) => (v.startsWith('-') ? v : `-${v}`))
                .join(',')
              return { ...filter, value: normalizedValue }
            }
            return filter
          })
        : undefined

    return {
      limit,
      sort_by: sortBy,
      sort_order: sortOrder,
      filters: outboundFilters,
    }
  }, [limit, sortBy, sortOrder, appliedFilters])

  React.useEffect(() => {
    onQueryParamsChange?.(queryParams)
  }, [onQueryParamsChange, queryParams])

  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    error,
    isError,
  } = queryResult

  // Update last updated timestamp when data changes
  React.useEffect(() => {
    if (queryData && !isFetching && !isLoading) {
      setLastUpdated(new Date())
    }
  }, [queryData, isFetching, isLoading])

  // Flatten all loaded API pages into a single array and derive total pages
  // purely from how many rows we've actually loaded. This matches the original
  // Call Logs behavior: we don't need to know the final "last page" up front.
  const { allData, totalPages } = React.useMemo(() => {
    if (!queryData?.pages) {
      return {
        allData: [],
        totalPages: 0,
      }
    }

    const allData = queryData.pages.flatMap(
      (page: { data: unknown[] } | unknown) => {
        if (page && typeof page === 'object' && 'data' in page) {
          return (page as { data: unknown[] }).data
        }
        return []
      }
    )

    // Calculate total pages for client-side pagination
    const totalPages =
      allData.length === 0
        ? 1
        : Math.max(1, Math.ceil(allData.length / pageSize))

    return {
      allData,
      totalPages,
    }
  }, [queryData, pageSize])

  // For features like "new row" highlighting that rely on a stable row key,
  // treat the loaded data as a generic array of records. The concrete views
  // (PatientsList, etc.) provide getRowKey when needed.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allFilteredData = allData as any[]

  const resolveRowKey = React.useCallback(
    (row: any, fallback: string): string => {
      const derivedKey = getRowKey?.(row) ?? row?.id
      return typeof derivedKey === 'string' && derivedKey.length > 0
        ? derivedKey
        : fallback
    },
    [getRowKey]
  )

  React.useEffect(() => {
    if (!enableMultiSelect) return

    const availableRowKeys = new Set(
      allFilteredData.map((row, idx) => resolveRowKey(row, `row-${idx}`))
    )

    setSelectedRowKeys((prev) => {
      const next = new Set([...prev].filter((key) => availableRowKeys.has(key)))
      return next.size === prev.size ? prev : next
    })
  }, [allFilteredData, enableMultiSelect, resolveRowKey])

  React.useEffect(() => {
    onSelectedRowsChangeRef.current = onSelectedRowsChange
  }, [onSelectedRowsChange])

  React.useEffect(() => {
    const selectedRowsCallback = onSelectedRowsChangeRef.current
    if (!selectedRowsCallback) return
    if (!enableMultiSelect) {
      selectedRowsCallback([])
      return
    }

    const selectedRows = allFilteredData.filter((row, idx) =>
      selectedRowKeys.has(resolveRowKey(row, `row-${idx}`))
    )
    selectedRowsCallback(selectedRows)
  }, [allFilteredData, enableMultiSelect, resolveRowKey, selectedRowKeys])

  // Detect new rows and highlight them
  React.useEffect(() => {
    if (!allFilteredData || allFilteredData.length === 0) return

    // Derive a generic row key via getRowKey when provided; otherwise fall
    // back to a simple "id" property if present.
    const currentRowKeys = new Set(
      allFilteredData
        .map((row) => {
          if (getRowKey) return getRowKey(row)
          return (row as { id?: string }).id
        })
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    )

    // Skip highlighting on initial load (when previousRowKeysRef is empty)
    if (previousRowKeysRef.current.size === 0) {
      previousRowKeysRef.current = currentRowKeys
      return
    }

    // Find new row keys (present in current but not in previous)
    const newIds = new Set<string>()
    currentRowKeys.forEach((id) => {
      if (!previousRowKeysRef.current.has(id)) {
        newIds.add(id)
      }
    })

    // Update state with new IDs
    if (newIds.size > 0) {
      setNewRowKeys(newIds)

      // Remove highlight after 15 seconds
      const timeout = setTimeout(() => {
        setNewRowKeys(new Set())
      }, 15000)

      return () => clearTimeout(timeout)
    }

    // Update previous row keys reference
    previousRowKeysRef.current = currentRowKeys
  }, [allFilteredData, getRowKey])

  // Get current page data
  const data = React.useMemo(() => {
    if (!allData || allData.length === 0) return []
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return allData.slice(startIndex, endIndex)
  }, [allData, currentPage, pageSize])

  const pageTotalCount = React.useMemo(() => {
    if (typeof totalCount === 'number' && Number.isFinite(totalCount)) {
      return totalCount
    }

    const firstPage = queryData?.pages?.[0] as
      | { pagination?: { total_count?: number; total?: number } }
      | undefined
    const paginationTotal = firstPage?.pagination?.total_count
    if (
      typeof paginationTotal === 'number' &&
      Number.isFinite(paginationTotal)
    ) {
      return paginationTotal
    }
    const legacyPaginationTotal = firstPage?.pagination?.total
    if (
      typeof legacyPaginationTotal === 'number' &&
      Number.isFinite(legacyPaginationTotal)
    ) {
      return legacyPaginationTotal
    }

    for (const row of data as Array<Record<string, unknown>>) {
      const candidate = row?.total_count
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return candidate
      }
    }
    return null
  }, [data, queryData?.pages, totalCount])

  const shouldShowLoadingState = React.useMemo(() => {
    const hasAnyPages = queryData?.pages && queryData.pages.length > 0

    if (!hasAnyPages) {
      // No pages loaded yet, show loading
      return isLoading || isFetching
    }

    // If we have some pages, but the current page slice is empty while we are
    // fetching more data (e.g. user navigated to a page that isn't loaded yet),
    // show the loading state instead of "No data found".
    if (isFetching && data.length === 0) {
      return true
    }

    // Otherwise, show the table (or "No ... found" message if filters remove all rows)
    return false
  }, [queryData?.pages, isLoading, isFetching, data.length])

  const expansionEnabled =
    Boolean(fetchSubRows) || Boolean(renderSubRows) || Boolean(canExpandRow)
  const shouldExpandRowOnClick =
    expansionEnabled && (expandRowOnClick ?? true) === true

  const loadSubRows = React.useCallback(
    async (parentKey: string, row: any) => {
      if (!fetchSubRows) return

      setSubRowsByParentKey((prev) => ({
        ...prev,
        [parentKey]: {
          isLoading: true,
          data: prev[parentKey]?.data ?? null,
          error: null,
        },
      }))

      try {
        const subRows = await fetchSubRows(row)
        setSubRowsByParentKey((prev) => ({
          ...prev,
          [parentKey]: {
            isLoading: false,
            data: Array.isArray(subRows) ? subRows : [],
            error: null,
          },
        }))
      } catch (err) {
        setSubRowsByParentKey((prev) => ({
          ...prev,
          [parentKey]: {
            isLoading: false,
            data: null,
            error:
              err instanceof Error ? err.message : 'Failed to load sub-rows',
          },
        }))
      }
    },
    [fetchSubRows]
  )

  // Load more pages if needed when navigating to a page that requires more data
  React.useEffect(() => {
    const itemsNeeded = currentPage * pageSize

    const rawItemsLoaded =
      queryData?.pages?.reduce(
        (total: number, page: { data: unknown[] } | unknown) => {
          if (page && typeof page === 'object' && 'data' in page) {
            return total + (page as { data: unknown[] }).data.length
          }
          return total
        },
        0
      ) || 0

    // Fetch more pages only when we need more items than we currently have
    if (itemsNeeded > rawItemsLoaded && hasNextPage && !isFetching) {
      // Fetch next page - cursor-based pagination requires sequential fetching
      fetchNextPage()
    }
  }, [
    currentPage,
    pageSize,
    hasNextPage,
    isFetching,
    fetchNextPage,
    queryData?.pages,
  ])

  // Reset to page 1 when filters or sorting change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [sortBy, sortOrder, appliedFilters])

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true, // Server-side sorting
    state: {
      sorting,
      columnVisibility,
    },
  })

  const leafColumnCount = React.useMemo(
    () =>
      (table.getAllLeafColumns().length || 1) + 2 + (enableMultiSelect ? 1 : 0),
    [enableMultiSelect, table]
  )
  const rowStartIndex = React.useMemo(
    () => (currentPage - 1) * pageSize,
    [currentPage, pageSize]
  )

  const toggleRowExpansion = React.useCallback(
    (row: any, parentKey: string, rowCanExpand: boolean) => {
      if (!rowCanExpand) return

      const isExpanded = expandedRowKeys.has(parentKey)
      setExpandedRowKeys((prev) => {
        const next = new Set(prev)
        if (next.has(parentKey)) {
          next.delete(parentKey)
        } else {
          next.add(parentKey)
        }
        return next
      })

      const currentlyLoading = subRowsByParentKey[parentKey]?.isLoading === true
      if (!isExpanded && !currentlyLoading) {
        void loadSubRows(parentKey, row)
      }
    },
    [expandedRowKeys, subRowsByParentKey, loadSubRows]
  )

  // Error state
  if (isError) {
    return (
      <div className="w-full p-6 overflow-auto">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="text-center">
            <p className="text-lg font-semibold text-red-600 mb-2">
              Failed to load data
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {error instanceof Error
                ? error.message
                : 'An unexpected error occurred'}
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Retry
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex flex-shrink-0 items-center justify-start gap-2 px-6 py-4">
        {showFilters && (
          <DropdownMenu
            open={filterDropdownOpen}
            onOpenChange={(open) => {
              setFilterDropdownOpen(open)
              // When opening dropdown, sync pending filters with applied filters
              if (open) {
                setFilters([...appliedFilters])
              }
            }}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  hasActiveFiltersValue && 'bg-gray-100 hover:bg-gray-200'
                )}>
                <Filter className="h-4 w-4 mr-2" /> Filter
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="start" className="w-[600px] p-4">
              <div className="space-y-3">
                {filters.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4 text-center">
                    No filters applied. Click "Add filter" to get started.
                  </div>
                ) : (
                  filters.map((filter) => {
                    const fieldConfig = getFieldConfig(filter.field)
                    const operators = getOperatorsForField(filter.field)

                    // Special handling for Type field - show checkboxes instead of operator + input
                    if (filter.field === 'type') {
                      const selectedTypes = filter.value
                        .split(',')
                        .map((v) => v.trim().toLowerCase())
                        .filter(Boolean)
                      const isInboundSelected =
                        selectedTypes.includes('inbound')
                      const isOutboundSelected =
                        selectedTypes.includes('outbound')

                      return (
                        <div
                          key={filter.id}
                          className="flex items-center gap-2">
                          <Select
                            value={filter.field}
                            onChange={(e) => {
                              const newField = e.target.value as FilterField
                              const newOperators =
                                getOperatorsForField(newField)
                              updateFilter(filter.id, {
                                field: newField,
                                operator: newOperators[0],
                                value: '',
                              })
                            }}
                            className="w-[150px]">
                            {filterFields.map((field) => (
                              <option key={field.value} value={field.value}>
                                {field.label}
                              </option>
                            ))}
                          </Select>

                          <div className="flex flex-1 items-center gap-4 rounded-md border px-3 py-2">
                            <label className="flex cursor-pointer items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isInboundSelected}
                                onChange={(e) => {
                                  const newTypes = e.target.checked
                                    ? [
                                        ...selectedTypes.filter(
                                          (t) => t !== 'inbound'
                                        ),
                                        'inbound',
                                      ]
                                    : selectedTypes.filter(
                                        (t) => t !== 'inbound'
                                      )
                                  updateFilter(filter.id, {
                                    value: newTypes.join(','),
                                  })
                                }}
                                className="h-4 w-4"
                              />
                              <span className="text-sm">Inbound</span>
                            </label>
                            <label className="flex cursor-pointer items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isOutboundSelected}
                                onChange={(e) => {
                                  const newTypes = e.target.checked
                                    ? [
                                        ...selectedTypes.filter(
                                          (t) => t !== 'outbound'
                                        ),
                                        'outbound',
                                      ]
                                    : selectedTypes.filter(
                                        (t) => t !== 'outbound'
                                      )
                                  updateFilter(filter.id, {
                                    value: newTypes.join(','),
                                  })
                                }}
                                className="h-4 w-4"
                              />
                              <span className="text-sm">Outbound</span>
                            </label>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFilter(filter.id)}
                            className="h-9 w-9">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )
                    }

                    // Special handling for select type fields (dropdown with predefined options)
                    if (
                      fieldConfig?.type === 'options' &&
                      fieldConfig.options
                    ) {
                      return (
                        <div
                          key={filter.id}
                          className="flex items-center gap-2">
                          <Select
                            value={filter.field}
                            onChange={(e) => {
                              const newField = e.target.value as string
                              const newOperators =
                                getOperatorsForField(newField)
                              updateFilter(filter.id, {
                                field: newField,
                                operator: newOperators[0],
                                value: '',
                              })
                            }}
                            className="w-[150px]">
                            {filterFields.map((field) => (
                              <option key={field.value} value={field.value}>
                                {field.label}
                              </option>
                            ))}
                          </Select>

                          {/* Operator dropdown for options */}
                          <Select
                            value={filter.operator}
                            onChange={(e) => {
                              updateFilter(filter.id, {
                                operator: e.target.value as FilterOperator,
                              })
                            }}
                            className="w-[120px]">
                            {operators.map((op) => (
                              <option key={op} value={op}>
                                {operatorLabels[op]}
                              </option>
                            ))}
                          </Select>

                          {filter.operator === 'in' ||
                          filter.operator === 'not_in' ? (
                            <div className="flex-1 rounded-md border px-3 py-2">
                              <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                                {fieldConfig.options.map((option) => {
                                  const selectedValues = filter.value
                                    .split(',')
                                    .map((v) => v.trim())
                                    .filter(Boolean)
                                  const isSelected = selectedValues.includes(
                                    option.value
                                  )
                                  return (
                                    <label
                                      key={option.value}
                                      className="flex cursor-pointer items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          const nextValues = e.target.checked
                                            ? [
                                                ...selectedValues.filter(
                                                  (v) => v !== option.value
                                                ),
                                                option.value,
                                              ]
                                            : selectedValues.filter(
                                                (v) => v !== option.value
                                              )
                                          updateFilter(filter.id, {
                                            value: nextValues.join(','),
                                          })
                                        }}
                                        className="h-4 w-4"
                                      />
                                      <span className="text-sm">
                                        {option.label}
                                      </span>
                                    </label>
                                  )
                                })}
                              </div>
                            </div>
                          ) : (
                            <Select
                              value={filter.value || ''}
                              onChange={(e) => {
                                updateFilter(filter.id, {
                                  value: e.target.value,
                                })
                              }}
                              className="flex-1">
                              <option value="">
                                Select {fieldConfig.label}
                              </option>
                              {fieldConfig.options.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          )}

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFilter(filter.id)}
                            className="h-9 w-9">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )
                    }

                    return (
                      <div key={filter.id} className="flex items-center gap-2">
                        <Select
                          value={filter.field}
                          onChange={(e) => {
                            const newField = e.target.value as FilterField
                            const newOperators = getOperatorsForField(newField)
                            updateFilter(filter.id, {
                              field: newField,
                              operator: newOperators[0],
                              value: '',
                            })
                          }}
                          className="w-[150px]">
                          {filterFields.map((field) => (
                            <option key={field.value} value={field.value}>
                              {field.label}
                            </option>
                          ))}
                        </Select>

                        {/* Operator dropdown and input (Type field uses checkboxes instead, handled above) */}
                        <Select
                          value={filter.operator}
                          onChange={(e) => {
                            updateFilter(filter.id, {
                              operator: e.target.value as FilterOperator,
                            })
                          }}
                          className="w-[120px]">
                          {operators.map((op) => (
                            <option key={op} value={op}>
                              {operatorLabels[op]}
                            </option>
                          ))}
                        </Select>

                        <Input
                          type={
                            fieldConfig?.type === 'date'
                              ? 'date'
                              : fieldConfig?.type === 'number'
                                ? 'number'
                                : 'text'
                          }
                          placeholder={
                            fieldConfig?.type === 'date'
                              ? 'Select date'
                              : fieldConfig?.type === 'number'
                                ? 'Enter a number'
                                : 'Enter a value'
                          }
                          value={filter.value}
                          onChange={(e) => {
                            updateFilter(filter.id, { value: e.target.value })
                          }}
                          onBlur={(e) => {
                            // Trim whitespace on blur to clean up pasted values
                            const trimmed = e.target.value.trim()
                            if (trimmed !== e.target.value) {
                              updateFilter(filter.id, { value: trimmed })
                            }
                          }}
                          className="flex-1"
                        />

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFilter(filter.id)}
                          className="h-9 w-9">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })
                )}
              </div>

              <DropdownMenuSeparator className="my-3" />

              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addFilter}
                  className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add filter
                </Button>

                <div className="flex items-center gap-2">
                  {hasActiveFiltersValue && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Clear
                    </Button>
                  )}
                  <Button
                    onClick={applyFilters}
                    size="sm"
                    disabled={!canApplyFilters}>
                    Apply filter
                  </Button>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">Columns</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              // Only show columns that the table marks as hideable.
              // Any table‑specific exclusions (e.g. action columns) should be
              // handled by setting enableHiding: false on those columns in
              // the feature modules (PatientsList, etc.).
              .filter((column) => column.getCanHide())
              .map((column) => {
                const metaLabel = (
                  column.columnDef.meta as { label?: string } | undefined
                )?.label
                const displayName = metaLabel ?? column.id

                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }>
                    {displayName}
                  </DropdownMenuCheckboxItem>
                )
              })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Refresh button */}
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={isFetching || !refreshQueryKey}
          title="Refresh data">
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
        </Button>

        {toolbarActions}

        {/* Last updated indicator */}
        <div className="text-sm text-muted-foreground ml-auto flex items-center gap-2">
          <span>Last updated {timeAgoText}</span>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 relative">
        {shouldShowLoadingState ? (
          <div className="flex items-center justify-center h-full min-h-[400px]">
            <div className="text-sm text-muted-foreground flex flex-col items-center gap-2">
              <Spinner />
              <div>Loading... This may take a moment for large datasets</div>
            </div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup, index) => {
                // Only render the first header group (top level) to avoid double headers
                if (index > 0) return null
                return (
                  <TableRow key={headerGroup.id}>
                    {enableMultiSelect && (
                      <TableHead className="w-10 px-2">
                        <input
                          type="checkbox"
                          aria-label="Select all rows"
                          checked={
                            table.getRowModel().rows.length > 0 &&
                            table
                              .getRowModel()
                              .rows.every((tableRow) =>
                                selectedRowKeys.has(
                                  resolveRowKey(tableRow.original, tableRow.id)
                                )
                              )
                          }
                          ref={(el) => {
                            if (!el) return
                            const anySelected = table
                              .getRowModel()
                              .rows.some((tableRow) =>
                                selectedRowKeys.has(
                                  resolveRowKey(tableRow.original, tableRow.id)
                                )
                              )
                            const allSelected =
                              table.getRowModel().rows.length > 0 &&
                              table
                                .getRowModel()
                                .rows.every((tableRow) =>
                                  selectedRowKeys.has(
                                    resolveRowKey(
                                      tableRow.original,
                                      tableRow.id
                                    )
                                  )
                                )
                            el.indeterminate = anySelected && !allSelected
                          }}
                          onChange={(event) => {
                            const checked = event.target.checked
                            setSelectedRowKeys((prev) => {
                              const next = new Set(prev)
                              table.getRowModel().rows.forEach((tableRow) => {
                                const key = resolveRowKey(
                                  tableRow.original,
                                  tableRow.id
                                )
                                if (checked) {
                                  next.add(key)
                                } else {
                                  next.delete(key)
                                }
                              })
                              return next
                            })
                          }}
                          onClick={(event) => event.stopPropagation()}
                          className="h-4 w-4"
                        />
                      </TableHead>
                    )}
                    <TableHead className="w-8 px-2" />
                    <TableHead className="w-14">#</TableHead>
                    {headerGroup.headers.map((header) => {
                      const colSpan =
                        header.colSpan > 1 ? header.colSpan : undefined
                      return (
                        <TableHead key={header.id} colSpan={colSpan}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableHeader>

            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row, rowIndex) => {
                  const rowKey =
                    getRowKey?.(row.original as any) ??
                    (row.original as any)?.id
                  const parentKey =
                    typeof rowKey === 'string' && rowKey.length > 0
                      ? rowKey
                      : row.id
                  const isNewRow =
                    typeof rowKey === 'string' && newRowKeys.has(rowKey)
                  const isExpanded = expandedRowKeys.has(parentKey)
                  const subRowState = subRowsByParentKey[parentKey]
                  const rowCanExpand =
                    shouldExpandRowOnClick &&
                    (canExpandRow ? canExpandRow(row.original) : true)
                  const rowIsInteractive = Boolean(onRowClick)

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        onClick={() => onRowClick?.(row.original)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            onRowClick?.(row.original)
                          }
                        }}
                        role={rowIsInteractive ? 'button' : undefined}
                        tabIndex={rowIsInteractive ? 0 : undefined}
                        className={cn(
                          isNewRow && 'animate-highlight-fade',
                          rowIsInteractive && 'cursor-pointer'
                        )}>
                        {enableMultiSelect && (
                          <TableCell className="w-10 px-2">
                            <input
                              type="checkbox"
                              aria-label="Select row"
                              checked={selectedRowKeys.has(parentKey)}
                              onChange={(event) => {
                                const checked = event.target.checked
                                setSelectedRowKeys((prev) => {
                                  const next = new Set(prev)
                                  if (checked) {
                                    next.add(parentKey)
                                  } else {
                                    next.delete(parentKey)
                                  }
                                  return next
                                })
                              }}
                              onClick={(event) => event.stopPropagation()}
                              className="h-4 w-4"
                            />
                          </TableCell>
                        )}
                        <TableCell className="w-8 px-2 align-middle">
                          {rowCanExpand ? (
                            <button
                              type="button"
                              aria-label={
                                isExpanded ? 'Collapse row' : 'Expand row'
                              }
                              onClick={(event) => {
                                event.stopPropagation()
                                toggleRowExpansion(
                                  row.original,
                                  parentKey,
                                  rowCanExpand
                                )
                              }}
                              className="inline-flex h-full items-center justify-center rounded-sm p-1 hover:bg-muted/60">
                              <ChevronRight
                                className={cn(
                                  'h-4 w-4 text-muted-foreground transition-transform duration-quick',
                                  isExpanded && 'rotate-90'
                                )}
                              />
                            </button>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                          {rowStartIndex + rowIndex + 1}
                        </TableCell>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>

                      {isExpanded && (
                        <TableRow>
                          <TableCell
                            colSpan={leafColumnCount}
                            className="bg-muted/20">
                            {subRowState?.isLoading ? (
                              <div className="text-sm text-muted-foreground py-2">
                                Loading related rows...
                              </div>
                            ) : subRowState?.error ? (
                              <div className="text-sm text-red-600 py-2">
                                {subRowState.error}
                              </div>
                            ) : renderSubRows ? (
                              renderSubRows(
                                subRowState?.data ?? [],
                                row.original
                              )
                            ) : (
                              <div className="text-sm text-muted-foreground py-2">
                                No sub-row renderer configured.
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={leafColumnCount}
                    className="h-24 text-center">
                    {emptyStateLabel ?? 'No data found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 pb-4 px-6 border-t flex-shrink-0">
        <div className="text-sm text-muted-foreground w-full md:w-auto md:whitespace-nowrap">
          {(() => {
            if (!queryData) return null
            // When there is at least one page of data, show the current page.
            // We rely on the "More results available" hint only for infinite
            // pagination where the API reports hasNextPage.
            const hasAnyData = data.length > 0
            if (!hasAnyData) return null

            const hasTotalCount = pageTotalCount !== null

            if (hasNextPage) {
              return (
                <>
                  Page {currentPage}
                  {hasTotalCount && <> | Total results: {pageTotalCount}</>}
                  {' | More results available'}
                </>
              )
            }

            return hasTotalCount ? (
              <>
                Page {currentPage} | Total results: {pageTotalCount}
              </>
            ) : (
              <>Page {currentPage}</>
            )
          })()}
        </div>

        {(totalPages > 1 || hasNextPage) && data.length > 0 && (
          <Pagination className="w-full justify-start md:w-auto md:justify-center">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (currentPage > 1) {
                      setCurrentPage(currentPage - 1)
                    }
                  }}
                  className={
                    currentPage === 1
                      ? 'pointer-events-none opacity-50'
                      : 'cursor-pointer'
                  }
                />
              </PaginationItem>
              {(() => {
                const pages: React.ReactNode[] = []

                // If 4 or fewer pages, show all pages without ellipsis
                if (totalPages <= 4) {
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(
                      <PaginationItem key={i}>
                        <PaginationLink
                          href="#"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault()
                            setCurrentPage(i)
                          }}
                          isActive={currentPage === i}
                          className="cursor-pointer">
                          {i}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  }
                } else if (currentPage > 3) {
                  // When there are 5+ pages and currentPage > 3, show 1, 2, 3, ..., currentPage, ..., last
                  // Show pages 1, 2, and 3
                  for (const pageNum of [1, 2, 3]) {
                    pages.push(
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          href="#"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault()
                            setCurrentPage(pageNum)
                          }}
                          isActive={currentPage === pageNum}
                          className="cursor-pointer">
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  }

                  // Show ellipsis after page 3 when currentPage > 3
                  pages.push(
                    <PaginationItem key="ellipsis-start">
                      <PaginationEllipsis />
                    </PaginationItem>
                  )

                  // Show current page (only if it's > 3)
                  if (currentPage > 3 && currentPage <= totalPages) {
                    pages.push(
                      <PaginationItem key={`page-${currentPage}`}>
                        <PaginationLink
                          href="#"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault()
                            setCurrentPage(currentPage)
                          }}
                          isActive
                          className="cursor-pointer">
                          {currentPage}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  }

                  // Show ellipsis after current page if there are more pages
                  // Only show last page if we're close to it (within 5 pages) to avoid slow jumps
                  const isNearLastPage = currentPage >= totalPages - 5
                  if (currentPage < totalPages - 1 && !isNearLastPage) {
                    pages.push(
                      <PaginationItem key="ellipsis-end">
                        <PaginationEllipsis />
                      </PaginationItem>
                    )
                  }

                  // Only show last page if we're close to it (within 5 pages) to avoid slow sequential loading
                  if (isNearLastPage && currentPage !== totalPages) {
                    pages.push(
                      <PaginationItem key={`page-${totalPages}`}>
                        <PaginationLink
                          href="#"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault()
                            setCurrentPage(totalPages)
                          }}
                          isActive={false}
                          className="cursor-pointer">
                          {totalPages}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  }
                } else {
                  // Original logic for pages 1-3
                  // Always show first page
                  pages.push(
                    <PaginationItem key={1}>
                      <PaginationLink
                        href="#"
                        size="icon"
                        onClick={(e) => {
                          e.preventDefault()
                          setCurrentPage(1)
                        }}
                        isActive={currentPage === 1}
                        className="cursor-pointer">
                        1
                      </PaginationLink>
                    </PaginationItem>
                  )

                  // Calculate pages to show around current page
                  let startPage = Math.max(2, currentPage - 1)
                  let endPage = Math.min(totalPages - 1, currentPage + 1)

                  // Adjust if we're near the start - show more pages at the beginning
                  if (currentPage <= 3) {
                    startPage = 2
                    endPage = Math.min(4, totalPages - 1)
                  }

                  // Show ellipsis before middle pages if there's a gap
                  // Show ellipsis when startPage > 2 (meaning we're skipping pages between 1 and startPage)
                  if (startPage > 2) {
                    pages.push(
                      <PaginationItem key="ellipsis-start">
                        <PaginationEllipsis />
                      </PaginationItem>
                    )
                  }

                  // Show pages around current page
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <PaginationItem key={i}>
                        <PaginationLink
                          href="#"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault()
                            setCurrentPage(i)
                          }}
                          isActive={currentPage === i}
                          className="cursor-pointer">
                          {i}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  }

                  // Show ellipsis after middle pages if there's a gap
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <PaginationItem key="ellipsis-end">
                        <PaginationEllipsis />
                      </PaginationItem>
                    )
                  }

                  // Always show last page
                  pages.push(
                    <PaginationItem key={totalPages}>
                      <PaginationLink
                        href="#"
                        size="icon"
                        onClick={(e) => {
                          e.preventDefault()
                          setCurrentPage(totalPages)
                        }}
                        isActive={currentPage === totalPages}
                        className="cursor-pointer">
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )
                }

                return pages
              })()}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  size="default"
                  onClick={(e) => {
                    e.preventDefault()
                    // Can navigate if there are more pages from API or more client pages
                    const canNavigate =
                      (currentPage < totalPages || hasNextPage) &&
                      data.length > 0 &&
                      data.length > 0

                    if (canNavigate) {
                      setCurrentPage(currentPage + 1)
                    }
                  }}
                  className={
                    currentPage >= totalPages && !hasNextPage
                      ? 'pointer-events-none opacity-50'
                      : data.length === 0
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  )
}
